/* LS cart drawer. Markup: sections/cart-drawer.liquid.
   Opens on the 'cart-drawer:refresh' event (fired by the product form in
   theme.js after an add) and when the header cart icon is clicked. Quantity
   changes go through /cart/change.js and the response's rendered section
   replaces the drawer contents.
   Dormant unless <html> has .ig-cart-drawer, which the Intelligems variant
   adds (or theme.liquid, when cart_type = drawer). Control keeps /cart. */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      document.addEventListener('cart-drawer:refresh', (evt) => {
        this.refresh().finally(() => {
          if (evt.detail && evt.detail.open) this.open();
        });
      });
      document.addEventListener('theme:cartchanged', () => {
        if (this.active) this.refresh();
      });

      // Capture phase so the theme's page-transition handler on
      // [data-cc-animate-click] never sees the header cart click.
      window.addEventListener('click', this.onHeaderCartClick.bind(this), true);
    }

    get active() {
      return document.documentElement.classList.contains('ig-cart-drawer');
    }

    get panel() {
      return this.querySelector('.cart-drawer__panel');
    }

    onHeaderCartClick(evt) {
      const link = evt.target.closest('#site-control a.cart:not(.nav-search)');
      if (!this.active || !link || document.body.classList.contains('template-cart')) return;
      if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.button !== 0) return;
      evt.preventDefault();
      evt.stopPropagation();
      this.open(link);
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.lastFocused = opener || document.activeElement;

      // Close the theme's own modals first (quick buy, search)
      if (document.body.classList.contains('modal-active') && window.closeThemeModal) {
        window.closeThemeModal(true);
      }

      this.hidden = false;
      document.documentElement.classList.add('cart-drawer-locked');
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        const close = this.querySelector('.cart-drawer__close');
        (close || this.panel).focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('cart-drawer-locked');
      setTimeout(() => {
        if (!this.classList.contains('is-open')) this.hidden = true;
      }, 350);
      if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus({ preventScroll: true });
    }

    onClick(evt) {
      if (evt.target.closest('[data-cart-drawer-close]')) {
        evt.preventDefault();
        this.close();
        return;
      }

      const item = evt.target.closest('.cart-drawer__item');
      if (!item) return;

      const step = evt.target.closest('[data-cart-drawer-qty]');
      if (step) {
        const quantity = parseInt(item.dataset.quantity, 10) + parseInt(step.dataset.cartDrawerQty, 10);
        this.change(item, Math.max(quantity, 0));
      } else if (evt.target.closest('[data-cart-drawer-remove]')) {
        this.change(item, 0);
      }
    }

    onKeydown(evt) {
      if (evt.key === 'Escape') {
        this.close();
        return;
      }
      if (evt.key !== 'Tab') return;

      // Keep focus inside the panel while it is open
      const focusable = Array.from(this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])'))
        .filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    }

    async change(item, quantity) {
      const max = item.dataset.max;
      if (max !== undefined && quantity > parseInt(max, 10)) return;

      this.classList.add('is-busy');
      try {
        const response = await fetch(`${this.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: parseInt(item.dataset.line, 10),
            quantity,
            sections: [this.sectionId],
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();

        if (!response.ok) {
          this.showError(item, data.description || data.message);
          return;
        }

        if (data.sections && data.sections[this.sectionId]) {
          this.render(data.sections[this.sectionId]);
        } else {
          await this.refresh();
        }
      } catch (err) {
        console.error(err);
        await this.refresh();
      } finally {
        this.classList.remove('is-busy');
      }
    }

    async refresh() {
      try {
        const response = await fetch(`${this.root}?sections=${this.sectionId}`);
        const data = await response.json();
        if (data[this.sectionId]) this.render(data[this.sectionId]);
      } catch (err) {
        console.error(err);
      }
    }

    render(html) {
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-contents]');
      const current = this.querySelector('[data-cart-drawer-contents]');
      if (!fresh || !current) return;

      const hadFocus = this.contains(document.activeElement);

      current.innerHTML = fresh.innerHTML;
      current.dataset.itemCount = fresh.dataset.itemCount;

      if (hadFocus) this.panel.focus({ preventScroll: true });

      this.updateHeaderCount(parseInt(fresh.dataset.itemCount, 10) || 0);
    }

    updateHeaderCount(count) {
      document.querySelectorAll('#site-control a.cart:not(.nav-search)').forEach((link) => {
        const badge = link.querySelector(':scope > div');
        if (badge) badge.textContent = count;
        const text = link.querySelector('.text-link');
        if (text) text.textContent = `${this.dataset.cartLabel} ${count > 0 ? `(${count})` : ''}`;
      });
    }

    showError(item, message) {
      const error = item.querySelector('.cart-drawer__error');
      if (!error || !message) return;
      error.textContent = message;
      error.hidden = false;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
